import React, { useEffect, useRef, useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import { useVesselStore } from 'src/context/VesselContext';
import {
    DefaultFlexStrechedWrapper,
    ElementWrapper,
    HighlightText,
    PanelSectionContent,
    PanelSubHeader,
} from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { ShipUtilizationInfo } from 'src/types/ViewModel/Stowage/ShipUtilizationInfo';
import { useRotationStore } from 'src/context/RotationContext';
import { roundTo0Decimal, roundTo1Decimal } from 'src/utils/numberHelper';
import { calculateVolume } from 'src/utils/stowage/stowageOperation/volumeUtils';
import { getAllTanks } from 'src/utils/stowage/tankPlanUtils';

import { getTankContentForCondition } from 'src/utils/stowage/manifest/tankPlanConditionUtils';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { SelectOption } from '../../../types/ViewModel/SelectOption';
import Decimal from 'decimal.js';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { getCargoTemperature } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import { usePositionFromClick } from 'src/hooks/stowage/usePositionFromClick';
import { PanelDimensions } from 'src/types/ViewModel/Stowage/PanelDimensions';

const initialVM: ShipUtilizationInfo = {
    shipDeadWeight: 0,
    cargoWeight: 0,
    nonCargoWeight: 0,
    cargoWeightOfMax: 0,
    totalMaxVolume: 0,
    totalUsedVolume: 0,
    totalAvailableVolume: 0,
    totalMaxWeight: 0,
    totalUsedWeight: 0,
    totalAvailableWeight: 0,
};

const UtilizationComponent: React.FC = () => {
    const { isUtilizationOpen, setIsUtilizationOpen } = useStowageStore();
    const { tankPlan, activeStowagePlan, selectedTankPlanCondition, mouseClickPosition } = useStowageStore();
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall } = useRotationStore();
    const [shipUtilizationVM, setShipUtilizationVM] = useState<ShipUtilizationInfo | null>(initialVM);
    const [deadweightOptions, setDeadweightOptions] = useState<SelectOption[]>([]);
    const [selectedDeadweightOption, setSelectedDeadweightOption] = useState<SelectOption | null>(null);
    const { selectedVessel } = useVesselStore();
    const panelRef = useRef(null);

    useEffect(() => {
        const checkIfClickedOutside = (event) => {
            if (isUtilizationOpen && panelRef.current && !panelRef.current.contains(event.target)) {
                setIsUtilizationOpen(false);
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);
        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [isUtilizationOpen]);

    useEffect(() => {
        if (!selectedVessel) return;

        const deadweightVM: any = [
            {
                label: 'Summer sea water',
                value: selectedVessel.deadweight,
            },
            {
                label: 'Winter sea water',
                value: selectedVessel.deadweightWinter,
            },
            {
                label: 'Tropical sea water',
                value: selectedVessel.deadweightTropical,
            },
        ];
        setSelectedDeadweightOption(deadweightVM[0]);
        setDeadweightOptions(deadweightVM);
        getMaxWeight();
        getMaxVolume();
    }, []);
    useEffect(() => {
        if (
            !selectedVessel ||
            !tankPlan ||
            !selectedTankPlanCondition ||
            (!selectedStowageRotationPortCall && !selectedStowageRotationBerthCall) ||
            !activeStowagePlan ||
            !activeStowagePlan.manifest
        ) {
            return;
        }
        getMaxWeight();
        getMaxVolume();
        getUsedVolume();
        getUsedWeight();
        getAvailableVolume();
        getAvailableWeight();
        getCargoWeightOfMax();
    }, [
        selectedVessel,
        tankPlan,
        selectedStowageRotationPortCall,
        selectedStowageRotationBerthCall,
        activeStowagePlan?.manifest,
        selectedTankPlanCondition,
    ]);

    useEffect(() => {
        if (!selectedVessel || !tankPlan) {
            return;
        }
        getMaxWeight();
        getMaxVolume();
        getAvailableVolume();
        getAvailableWeight();
    }, [selectedVessel, tankPlan]);

    const getMaxVolume = () => {
        let _totalVolume = 0;

        if (tankPlan) {
            const allTanks = getAllTanks(tankPlan);

            allTanks.forEach((t) => {
                _totalVolume += t?.volumeAt98Percent;
            });
        }

        shipUtilizationVM.totalMaxVolume = roundTo0Decimal(_totalVolume);
        setShipUtilizationVM({ ...shipUtilizationVM });
    };

    const getMaxWeight = () => {
        shipUtilizationVM.totalMaxWeight = roundTo0Decimal(selectedVessel.deadweight);
        setShipUtilizationVM({ ...shipUtilizationVM });
    };

    const getUsedVolume = () => {
        const _usedVolumeInTanks = calculateUsedVolumeInTanks(
            activeStowagePlan,
            selectedStowageRotationPortCall,
            selectedStowageRotationBerthCall,
            selectedTankPlanCondition
        );

        shipUtilizationVM.totalUsedVolume = _usedVolumeInTanks ? roundTo0Decimal(_usedVolumeInTanks.toNumber()) : 0;
        setShipUtilizationVM({ ...shipUtilizationVM });
    };

    const getUsedWeight = () => {
        let usedWeight = new Decimal(0);

        if (!tankPlan) return;

        const allTanks = getAllTanks(tankPlan);
        allTanks.forEach((t) => {
            const tankContent = getTankContentForCondition(
                activeStowagePlan,
                selectedStowageRotationPortCall,
                selectedStowageRotationBerthCall,
                selectedTankPlanCondition,
                t
            );

            usedWeight = usedWeight.plus(
                tankContent?.cargoContents.reduce((acc, cargoContent) => acc.plus(cargoContent.weight), new Decimal(0)) ?? new Decimal(0)
            );
        });

        shipUtilizationVM.cargoWeight = roundTo0Decimal(usedWeight.toNumber());
        usedWeight = usedWeight.plus(new Decimal(shipUtilizationVM.nonCargoWeight));

        // if (usedWeight.gt(0)) {
        //     usedWeight = usedWeight.plus(new Decimal(shipUtilizationVM.nonCargoWeight));
        // } else {
        //     usedWeight = new Decimal(0);
        // }

        shipUtilizationVM.totalUsedWeight = roundTo0Decimal(usedWeight.toNumber());
        setShipUtilizationVM({ ...shipUtilizationVM });
    };

    const calculateUsedVolumeInTanks = (
        activeStowagePlan: StowagePlan,
        portCall: RotationPortCall,
        berthCall: RotationBerthCall,
        tankPlanCondition: TankPlanCondition
    ): Decimal => {
        let _totalVolume = new Decimal(0);

        const allTanks = getAllTanks(tankPlan);
        allTanks.forEach((t) => {
            const tankContent = getTankContentForCondition(activeStowagePlan, portCall, berthCall, tankPlanCondition, t);
            tankContent?.cargoContents.forEach((cargoContent) => {
                const cargoVolumeInTank = calculateVolume(
                    cargoContent.weight,
                    cargoContent.cargo,
                    getCargoTemperature(activeStowagePlan, cargoContent.cargo, t)
                );
                _totalVolume = _totalVolume.add(cargoVolumeInTank);
            });
        });

        return _totalVolume;
    };

    const getAvailableVolume = () => {
        shipUtilizationVM.totalAvailableVolume = shipUtilizationVM.totalMaxVolume - shipUtilizationVM.totalUsedVolume;
        setShipUtilizationVM({ ...shipUtilizationVM });
    };

    const getAvailableWeight = () => {
        shipUtilizationVM.totalAvailableWeight = roundTo0Decimal(shipUtilizationVM.totalMaxWeight - shipUtilizationVM.totalUsedWeight);
        setShipUtilizationVM({ ...shipUtilizationVM });
    };

    const getCargoWeightOfMax = () => {
        const _cargoWeightOfMax = (shipUtilizationVM.cargoWeight / shipUtilizationVM.totalMaxWeight) * 100;
        shipUtilizationVM.cargoWeightOfMax = roundTo1Decimal(_cargoWeightOfMax);
        setShipUtilizationVM({ ...shipUtilizationVM });
    };

    const onChangeDeadweightOption = (changeObject: InputChangeData) => {
        setSelectedDeadweightOption(changeObject.event.target.value);
        shipUtilizationVM.totalMaxWeight = Number(changeObject.event.target.value);
        setShipUtilizationVM({ ...shipUtilizationVM });

        getAvailableWeight();
        getUsedWeight();
        getCargoWeightOfMax();
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const targetValue = changeObject.event.target.value;
        const _nonCargoWeight = targetValue ? +targetValue : 0;

        shipUtilizationVM.nonCargoWeight = _nonCargoWeight;
        setShipUtilizationVM({ ...shipUtilizationVM });
        getUsedWeight();
        getAvailableWeight();
    };

    const onClosePanel = () => {
        setIsUtilizationOpen(false);
    };

    const panelDimensions: PanelDimensions = {
        width: PANEL_CONSTANTS.SMALL_WIDTH,
        height: 350,
    };

    const panelPosition = usePositionFromClick(mouseClickPosition, panelDimensions);

    return (
        isUtilizationOpen &&
        shipUtilizationVM && (
            <OdfjellPanel
                headerTitle={'Ship utilization'}
                defaultPosition="right"
                initialPosition={mouseClickPosition ? panelPosition : undefined}
                panelWidth={PANEL_CONSTANTS.SMALL_WIDTH}
                preventOutsideClose={false}
                onCancel={onClosePanel}
                onAction={onClosePanel}
                actionButtonText="Close"
            >
                <PanelSectionContent>
                    <PanelSubHeader>Weight details</PanelSubHeader>
                    <OdfjellInputComponent
                        isReadOnly={true}
                        type={'number'}
                        description="Cargo weight"
                        value={shipUtilizationVM?.cargoWeight}
                        name={'cargoWeight'}
                        onChange={null}
                        unit={'MT'}
                        leaveBlank={true}
                        key={'input_cargo_weight'}
                    />
                    <OdfjellInputComponent
                        isReadOnly={false}
                        type={'number'}
                        description="Non cargo weight"
                        value={shipUtilizationVM?.nonCargoWeight}
                        name={'nonCargoWeight'}
                        onChange={onInputChange}
                        unit={'MT'}
                        leaveBlank={true}
                        key={'input_non_cargo_weight'}
                    />
                </PanelSectionContent>

                <PanelSectionContent>
                    <PanelSubHeader>Zone</PanelSubHeader>
                    <OdfjellInputComponent
                        isReadOnly={false}
                        description="Load line"
                        type={'select'}
                        name={'loadLine'}
                        options={deadweightOptions}
                        value={selectedDeadweightOption}
                        dataObject={shipUtilizationVM}
                        onChange={onChangeDeadweightOption}
                        leaveBlank={true}
                        disableUnit={true}
                        key={'input_load_line'}
                    />
                </PanelSectionContent>
                <PanelSectionContent>
                    <PanelSubHeader>Deadweight</PanelSubHeader>
                    <DefaultFlexStrechedWrapper>
                        <span>Max</span>
                        <span>{shipUtilizationVM?.totalMaxWeight} MT</span>
                    </DefaultFlexStrechedWrapper>
                    <DefaultFlexStrechedWrapper>
                        <span>Used</span>
                        <span>{shipUtilizationVM?.totalUsedWeight} MT</span>
                    </DefaultFlexStrechedWrapper>
                    <DefaultFlexStrechedWrapper>
                        <span>Available</span>
                        <span>{shipUtilizationVM?.totalAvailableWeight} MT</span>
                    </DefaultFlexStrechedWrapper>
                </PanelSectionContent>
                <PanelSectionContent>
                    <PanelSubHeader>Tank Volume</PanelSubHeader>
                    <DefaultFlexStrechedWrapper>
                        <span>Max</span>
                        <span>{shipUtilizationVM?.totalMaxVolume} m³</span>
                    </DefaultFlexStrechedWrapper>
                    <DefaultFlexStrechedWrapper>
                        <span>Used</span>
                        <span>{shipUtilizationVM?.totalUsedVolume} m³</span>
                    </DefaultFlexStrechedWrapper>
                    <DefaultFlexStrechedWrapper>
                        <span>Available</span>
                        <span>{shipUtilizationVM?.totalAvailableVolume} m³</span>
                    </DefaultFlexStrechedWrapper>
                </PanelSectionContent>
                <PanelSectionContent>
                    <ElementWrapper>
                        <HighlightText>
                            <OdfjellInputComponent
                                isReadOnly={true}
                                type={'number'}
                                description="Cargo weight / max"
                                value={shipUtilizationVM?.cargoWeightOfMax}
                                name={'cargoWeightOfMax'}
                                onChange={null}
                                unit={'%'}
                                leaveBlank={true}
                                key={'input_cargo_weigh_of_max'}
                            />
                        </HighlightText>
                    </ElementWrapper>
                </PanelSectionContent>
            </OdfjellPanel>
        )
    );
};

export default UtilizationComponent;
